import { NATIONAL_NUMBERING_PLANS } from 'src/core/constants/national-numbering-plans.constant';
import { NationalNumberingPlan } from 'src/core/models/phone-number/national-numbering-plan.model';

/*
 * Frontend port of backend `PhoneNumberConverter` (`Domain/Helpers/PhoneNumberConverter.cs`):
 * `normalizePhoneNumberToE164` mirrors `TryNormalizeToE164` and `formatPhoneNumberToE123` mirrors
 * `FormatAsE123`, so what the contact form validates and shows is exactly what the backend stores.
 * Keep both sides in step. The functions are pure: the tenant's default country code is always a
 * parameter (resolved by `LocalizationDisplayService.defaultCountryCode`), never a constant here.
 */

/**
 * Countries whose international numbers may still carry the "(0)" trunk digit after the country
 * code (`+44 (0) 20…`): derived from the numbering plans, so there is no second list to keep in step.
 */
const TRUNK_ZERO_COUNTRY_CODES: readonly string[] = NATIONAL_NUMBERING_PLANS.filter(
  plan => plan.trunkPrefix === '0'
).map(plan => plan.countryCode);

/** Shortest and longest digit count of a normalized E.164 number (without the plus). */
const E164_MIN_DIGITS = 8;
const E164_MAX_DIGITS = 15;

/** Digits a national number needs beyond the country code to be read as already international. */
const MIN_DIGITS_AFTER_COUNTRY_CODE = 6;

/**
 * Gets the national numbering plan of a country code.
 *
 * @param countryCode a calling code without a plus sign, e.g. `90`.
 * @returns the plan, or `null` for a country the platform has no national rules for.
 */
export function getNationalNumberingPlan(countryCode: string): NationalNumberingPlan | null {
  return NATIONAL_NUMBERING_PLANS.find(plan => plan.countryCode === countryCode) ?? null;
}

/**
 * Parses `raw` into canonical E.164 (`+905551112233`), applying `defaultCountryCode` to numbers
 * written in national form. Handles the international prefixes (`+`/`00`), the "(0)" trunk digit
 * after a trunk-zero country code, and formatting characters (whitespace, `- ( ) .`).
 *
 * National forms (a bare national number, or one with the trunk prefix) are only understood when the
 * default country has a numbering plan; for any other country only international forms are accepted.
 *
 * @param raw the number as entered.
 * @param defaultCountryCode the tenant's calling code without a plus sign, e.g. `90`.
 * @returns the E.164 number, or `null` when the input does not resolve to one.
 */
export function normalizePhoneNumberToE164(
  raw: string | null | undefined,
  defaultCountryCode: string
): string | null {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed || !containsOnlySupportedCharacters(trimmed)) {
    return null;
  }

  let compact = removeFormatting(trimmed);
  if (!compact) {
    return null;
  }

  if (compact.startsWith('00')) {
    compact = `+${compact.slice(2)}`;
  }

  if (compact.startsWith('+')) {
    return normalizeInternationalDigits(compact.slice(1));
  }

  if (!/^\d+$/.test(compact)) {
    return null;
  }

  if (
    compact.startsWith(defaultCountryCode) &&
    compact.length >= defaultCountryCode.length + MIN_DIGITS_AFTER_COUNTRY_CODE &&
    compact.length <= E164_MAX_DIGITS
  ) {
    return normalizeInternationalDigits(compact);
  }

  const plan = getNationalNumberingPlan(defaultCountryCode);
  if (!plan) {
    return null;
  }

  return normalizeNationalDigits(compact, plan);
}

/**
 * Renders a canonical E.164 number as its E.123 display form (`+90 555 111 22 33`), grouping the
 * national digits by country-specific rules with a generic fallback. Input too short to be E.164 is
 * returned unchanged.
 */
export function formatPhoneNumberToE123(e164: string | null | undefined): string {
  const value = e164?.trim() ?? '';
  if (!value) {
    return '';
  }

  const digits = value.startsWith('+') ? value.slice(1) : value;
  if (digits.length < 7) {
    return value;
  }

  if (digits.startsWith('90') && digits.length === 12) {
    return `+90 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 10)} ${digits.slice(10, 12)}`;
  }

  if (digits.startsWith('44') && digits.length >= 12) {
    return `+44 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }

  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  }

  if (digits.startsWith('49') && digits.length >= 11) {
    return `+49 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  }

  const countryCodeLength = digits.length > 10 ? (digits[0] === '1' ? 1 : digits.length > 11 ? 3 : 2) : 1;
  const countryCode = digits.slice(0, countryCodeLength);
  const rest = digits.slice(countryCodeLength);

  if (!rest) {
    return `+${countryCode}`;
  }

  if (rest.length <= 4) {
    return `+${countryCode} ${rest}`;
  }

  return `+${countryCode} ${formatNationalDigitsInGroupsOfThree(rest)}`;
}

/** Mirrors backend `TryNormalizeNational`: a bare NSN first, then the trunk prefix + NSN. */
function normalizeNationalDigits(digits: string, plan: NationalNumberingPlan): string | null {
  const isBareNationalNumber =
    digits.length >= plan.nationalNumberMinLength && digits.length <= plan.nationalNumberMaxLength;
  if (isBareNationalNumber) {
    return normalizeInternationalDigits(`${plan.countryCode}${digits}`);
  }

  const trunkLength = plan.trunkPrefix.length;
  const isTrunkedNationalNumber =
    digits.length >= trunkLength + plan.nationalNumberMinLength &&
    digits.length <= trunkLength + plan.nationalNumberMaxLength &&
    digits.startsWith(plan.trunkPrefix);
  if (isTrunkedNationalNumber) {
    return normalizeInternationalDigits(`${plan.countryCode}${digits.slice(trunkLength)}`);
  }

  return null;
}

function normalizeInternationalDigits(digits: string): string | null {
  if (!/^\d+$/.test(digits)) {
    return null;
  }

  const normalizedDigits = removeTrunkZeroAfterCountryCode(digits);
  if (
    normalizedDigits.length < E164_MIN_DIGITS ||
    normalizedDigits.length > E164_MAX_DIGITS ||
    normalizedDigits.startsWith('0')
  ) {
    return null;
  }

  return `+${normalizedDigits}`;
}

function removeTrunkZeroAfterCountryCode(digits: string): string {
  for (const countryCode of TRUNK_ZERO_COUNTRY_CODES) {
    if (!digits.startsWith(countryCode)) {
      continue;
    }

    const nationalStart = countryCode.length;
    if (digits.length > nationalStart && digits[nationalStart] === '0') {
      return `${digits.slice(0, nationalStart)}${digits.slice(nationalStart + 1)}`;
    }

    return digits;
  }

  return digits;
}

function removeFormatting(raw: string): string {
  let compact = '';
  for (const current of raw) {
    if (/\d/.test(current) || (current === '+' && compact.length === 0)) {
      compact += current;
    }
  }

  return compact;
}

function containsOnlySupportedCharacters(raw: string): boolean {
  for (const current of raw) {
    if (/\d/.test(current) || /\s/.test(current)) {
      continue;
    }

    if (current === '+' || current === '-' || current === '(' || current === ')' || current === '.') {
      continue;
    }

    return false;
  }

  return true;
}

function formatNationalDigitsInGroupsOfThree(nationalDigits: string): string {
  if (nationalDigits.length <= 4) {
    return nationalDigits;
  }

  const parts: string[] = [];
  for (let index = 0; index < nationalDigits.length; index += 3) {
    parts.push(nationalDigits.slice(index, index + 3));
  }

  return parts.join(' ');
}
